import { useState } from "react";
import { Crown, Check, X, Zap, Heart, Clock } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const DONATION_TIERS = [49, 99, 199];

export const UpgradeModal = ({ open, onClose }) => {
  const { t } = useLang();
  const [tab, setTab] = useState("premium");
  const FREE_FEATURES = [t("feat.free1"), t("feat.free2"), t("feat.free3"), t("feat.free4")];
  const PREMIUM_FEATURES = [t("feat.prem1"), t("feat.prem2"), t("feat.prem3"), t("feat.prem4"), t("feat.prem5")];

  const comingSoon = () => toast.info(t("upgrade.comingSoon"));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="sm:max-w-2xl border-white/10 bg-[#0C0E15] p-0 overflow-hidden [&>button]:hidden"
        data-testid="upgrade-modal"
      >
        <DialogTitle className="sr-only">Upgrade to Premium</DialogTitle>
        <DialogDescription className="sr-only">Compare Free and Premium plans and support the app.</DialogDescription>

        <div className="relative p-6 sm:p-8">
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-48 w-96 rounded-full bg-violet-600/20 blur-3xl pointer-events-none" />
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-slate-500 hover:text-white transition-colors z-10"
            data-testid="upgrade-close-button"
          >
            <X size={20} />
          </button>

          <div className="text-center relative">
            <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 grid place-items-center mx-auto mb-3 vg-glow">
              <Crown size={22} className="text-white" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-head font-extrabold">{t("upgrade.title")}</h2>
            <p className="text-sm text-slate-400 mt-1.5">{t("upgrade.subtitle")}</p>
          </div>

          <div className="flex gap-1 p-1 rounded-xl bg-black/30 border border-white/5 mt-6 mb-6 max-w-xs mx-auto">
            <button
              onClick={() => setTab("premium")}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-1.5 ${
                tab === "premium" ? "bg-gradient-to-r from-violet-600 to-indigo-600 text-white" : "text-slate-400"
              }`}
              data-testid="upgrade-tab-premium"
            >
              <Crown size={14} /> {t("upgrade.plans")}
            </button>
            <button
              onClick={() => setTab("donate")}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-1.5 ${
                tab === "donate" ? "bg-gradient-to-r from-rose-500 to-pink-600 text-white" : "text-slate-400"
              }`}
              data-testid="upgrade-tab-donate"
            >
              <Heart size={14} /> {t("upgrade.donate")}
            </button>
          </div>

          {tab === "premium" ? (
            <div className="grid sm:grid-cols-2 gap-4">
              {/* Free */}
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                <p className="text-sm font-mono uppercase tracking-widest text-slate-500">{t("plan.free")}</p>
                <p className="mt-2 text-3xl font-head font-bold">₹0</p>
                <p className="text-xs text-slate-500 mb-4">{t("plan.forever")}</p>
                <ul className="space-y-2.5">
                  {FREE_FEATURES.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                      <Check size={15} className="text-slate-500 mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Premium */}
              <div className="relative rounded-2xl border border-violet-500/50 bg-gradient-to-b from-violet-600/15 to-transparent p-5 vg-glow">
                <span className="absolute -top-2.5 right-4 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-black">
                  {t("plan.popular")}
                </span>
                <p className="text-sm font-mono uppercase tracking-widest text-violet-300">{t("plan.premium")}</p>
                <p className="mt-2 text-3xl font-head font-bold">
                  ₹49<span className="text-sm font-normal text-slate-400">/{t("plan.month")}</span>
                </p>
                <p className="text-xs text-slate-500 mb-4">{t("plan.cancel")}</p>
                <ul className="space-y-2.5 mb-5">
                  {PREMIUM_FEATURES.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-white">
                      <Check size={15} className="text-violet-400 mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={comingSoon}
                  className="w-full h-11 rounded-xl bg-gradient-to-r from-violet-600 via-indigo-600 to-cyan-500 font-semibold text-white flex items-center justify-center gap-2 hover:opacity-95 transition-opacity"
                  data-testid="upgrade-premium-button"
                >
                  <Zap size={16} /> {t("upgrade.cta")}
                </button>
                <p className="flex items-center justify-center gap-1.5 text-[11px] text-amber-400/90 mt-2.5">
                  <Clock size={12} /> {t("upgrade.comingSoon")}
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <p className="text-sm text-slate-400 max-w-sm mx-auto mb-5">{t("donate.desc")}</p>
              <div className="flex justify-center gap-3 mb-4">
                {DONATION_TIERS.map((amt) => (
                  <button
                    key={amt}
                    onClick={comingSoon}
                    className="flex-1 max-w-[120px] py-4 rounded-2xl border border-white/10 bg-white/[0.03] hover:border-rose-500/50 hover:bg-rose-500/5 transition-all"
                    data-testid={`donate-tier-${amt}`}
                  >
                    <span className="block text-2xl font-head font-bold">₹{amt}</span>
                    <span className="block text-[11px] text-slate-500 mt-1">
                      <Heart size={11} className="inline" />
                    </span>
                  </button>
                ))}
              </div>
              <p className="flex items-center justify-center gap-1.5 text-[11px] text-amber-400/90">
                <Clock size={12} /> {t("upgrade.comingSoon")}
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
